import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  deleteField,
} from "firebase/firestore";
import { db } from "./init";
import { Comment } from "@/types/comment";
import { convertTimestampToDate } from "./firestore";

/**
 * Firestore 評論數據類型（包含 Timestamp）
 */
interface FirestoreCommentData {
  videoId?: string;
  userId?: string;
  userName?: string;
  userPhotoURL?: string;
  content?: string;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
  isEdited?: boolean;
  isHidden?: boolean;
  isDeleted?: boolean;
  hiddenBy?: string;
  hiddenAt?: Timestamp | Date | null;
  hiddenReason?: string;
  replyTo?: string;
  replyCount?: number;
  reportCount?: number;
  likeCount?: number;
}

/**
 * 轉換評論數據（處理 Timestamp）
 */
function convertCommentData(
  data: FirestoreCommentData,
  id: string
): Omit<Comment, "id"> {
  return {
    videoId: data.videoId || "",
    userId: data.userId || "",
    userName: data.userName || "",
    userPhotoURL: data.userPhotoURL,
    content: data.content || "",
    createdAt: convertTimestampToDate(data.createdAt) as Date | undefined,
    updatedAt: convertTimestampToDate(data.updatedAt) as Date | undefined,
    isEdited: data.isEdited ?? false,
    isHidden: data.isHidden ?? false,
    isDeleted: data.isDeleted ?? false,
    hiddenBy: data.hiddenBy,
    hiddenAt: convertTimestampToDate(data.hiddenAt) as Date | undefined,
    hiddenReason: data.hiddenReason,
    replyTo: data.replyTo,
    replyCount: data.replyCount ?? 0,
    reportCount: data.reportCount ?? 0,
    likeCount: data.likeCount ?? 0,
  };
}

/**
 * 獲取所有評論（排除已刪除的）
 */
export async function getComments(
  filters?: {
    videoId?: string;
    userId?: string;
    isHidden?: boolean;
    isDeleted?: boolean;
  }
): Promise<Comment[]> {
  try {
    const commentsRef = collection(db, "comments");
    const constraints: any[] = [];

    // 添加篩選條件
    if (filters?.videoId) {
      constraints.push(where("videoId", "==", filters.videoId));
    }
    if (filters?.userId) {
      constraints.push(where("userId", "==", filters.userId));
    }
    if (filters?.isHidden !== undefined) {
      constraints.push(where("isHidden", "==", filters.isHidden));
    }
    if (filters?.isDeleted !== undefined) {
      constraints.push(where("isDeleted", "==", filters.isDeleted));
    } else {
      // 預設排除已刪除的評論
      constraints.push(where("isDeleted", "==", false));
    }

    let querySnapshot;
    let useClientSort = false;

    try {
      const q = query(
        commentsRef,
        ...constraints,
        orderBy("createdAt", "desc")
      );
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn(
        "Failed to order by 'createdAt', using basic query with client-side sort:",
        orderError
      );
      const q = query(commentsRef, ...constraints);
      querySnapshot = await getDocs(q);
      useClientSort = true;
    }

    const comments = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertCommentData(doc.data(), doc.id),
    })) as Comment[];

    // 如果沒有使用 orderBy，在客戶端排序
    if (useClientSort) {
      comments.sort((a, b) => {
        const aTime = a.createdAt?.getTime() || 0;
        const bTime = b.createdAt?.getTime() || 0;
        return bTime - aTime; // 降序
      });
    }

    return comments;
  } catch (error) {
    console.error("Error getting comments:", error);
    throw error;
  }
}

/**
 * 獲取單個評論
 */
export async function getComment(id: string): Promise<Comment | null> {
  try {
    const commentRef = doc(db, "comments", id);
    const commentSnap = await getDoc(commentRef);

    if (commentSnap.exists()) {
      return {
        id: commentSnap.id,
        ...convertCommentData(commentSnap.data(), commentSnap.id),
      } as Comment;
    }

    return null;
  } catch (error) {
    console.error("Error getting comment:", error);
    throw error;
  }
}

/**
 * 隱藏評論
 */
export async function hideComment(
  id: string,
  reason?: string,
  hiddenBy?: string
): Promise<void> {
  try {
    const commentRef = doc(db, "comments", id);
    const updateData: {
      isHidden: boolean;
      hiddenAt: Timestamp;
      hiddenBy?: string;
      hiddenReason?: string;
    } = {
      isHidden: true,
      hiddenAt: Timestamp.now(),
    };

    if (hiddenBy) updateData.hiddenBy = hiddenBy;
    if (reason) updateData.hiddenReason = reason;

    await updateDoc(commentRef, updateData);
  } catch (error) {
    console.error("Error hiding comment:", error);
    throw error;
  }
}

/**
 * 顯示評論（取消隱藏）
 */
export async function unhideComment(id: string): Promise<void> {
  try {
    const commentRef = doc(db, "comments", id);
    await updateDoc(commentRef, {
      isHidden: false,
      hiddenBy: deleteField(),
      hiddenAt: deleteField(),
      hiddenReason: deleteField(),
    });
  } catch (error) {
    console.error("Error unhiding comment:", error);
    throw error;
  }
}

/**
 * 刪除評論（軟刪除）
 */
export async function deleteComment(id: string): Promise<void> {
  try {
    const commentRef = doc(db, "comments", id);
    await updateDoc(commentRef, {
      isDeleted: true,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
}

/**
 * 永久刪除評論（硬刪除，僅管理員使用）
 */
export async function permanentlyDeleteComment(id: string): Promise<void> {
  try {
    const commentRef = doc(db, "comments", id);
    await deleteDoc(commentRef);
  } catch (error) {
    console.error("Error permanently deleting comment:", error);
    throw error;
  }
}

